import { Link } from "@tanstack/react-router";
import { PageHero } from "@/components/PageHero";
import { SectionHeading } from "@/components/SectionHeading";
import { CTASection } from "@/components/CTASection";
import { MultiStepQuoteForm } from "@/components/MultiStepQuoteForm";
import { FAQAccordion } from "@/components/FAQAccordion";
import { SERVICES, CITIES } from "@/lib/site";
import { type Faq, withObjectionFaqs } from "@/lib/faqs";
import { Check, MapPin } from "lucide-react";
import commercialImg from "@/assets/commercial-floor.jpg";

export type CityPageProps = {
  slug: string;
  name: string;
  state: string;
  region: string;
  landmarks: readonly string[];
  intro: string;
  whyLocal: string;
  faqs: Faq[];
  h1: string;
};

export function CityPageLayout(p: CityPageProps) {
  const nearby = CITIES.filter((c) => c.slug !== p.slug).slice(0, 6);
  const display = `${p.name}, ${p.state}`;
  return (
    <>
      <PageHero
        eyebrow={`Serving ${p.region}`}
        title={p.h1}
        body={p.intro}
        image={commercialImg}
      />

      <section className="section-light py-20 md:py-24"><div className="container-x grid gap-12 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-8">
          <article>
            <h2 className="text-2xl md:text-3xl text-silver-light">Premium Epoxy & Polyaspartic Floors For {display}</h2>
            <span className="copper-rule mt-3 mb-5" />
            <p className="text-silver leading-relaxed whitespace-pre-line">{p.whyLocal}</p>
          </article>

          <article>
            <h2 className="text-2xl md:text-3xl text-silver-light">Neighborhoods We Serve in {p.name}</h2>
            <span className="copper-rule mt-3 mb-5" />
            <ul className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {p.landmarks.map((l) => (
                <li key={l} className="flex items-center gap-2 text-silver">
                  <MapPin className="h-4 w-4 text-copper" /> {l}
                </li>
              ))}
            </ul>
          </article>

          <article>
            <h2 className="text-2xl md:text-3xl text-silver-light">Coating Systems Available in {p.name}</h2>
            <span className="copper-rule mt-3 mb-5" />
            <div className="grid gap-3 sm:grid-cols-2">
              {SERVICES.map((s) => (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}` as string}
                  className="rounded-sm border border-border bg-charcoal p-4 hover:border-copper"
                >
                  <p className="text-silver-light font-semibold">{s.name}</p>
                  <p className="mt-1 text-sm text-silver">{s.short}</p>
                </Link>
              ))}
            </div>
          </article>
        </div>

        <aside className="space-y-6">
          <div className="rounded-sm border border-border bg-charcoal p-6">
            <p className="text-xs font-bold uppercase tracking-widest text-copper">Why {p.name} Chooses MLC Epoxy Floors</p>
            <ul className="mt-4 space-y-2.5 text-sm text-silver">
              {[
                "Local Airdrie-based crew",
                "Diamond grinding on every job",
                "Polyaspartic UV-stable topcoat",
                "Fixed written quotes",
                "Fully insured workmanship warranty",
              ].map((b) => (
                <li key={b} className="flex items-start gap-2">
                  <Check className="h-4 w-4 mt-0.5 text-copper shrink-0" /> {b}
                </li>
              ))}
            </ul>
          </div>
          <Link
            to="/quote"
            className="block rounded-sm bg-copper px-6 py-4 text-center text-sm font-bold uppercase tracking-wider text-white hover:bg-copper-hover"
          >
            Get Free {p.name} Quote
          </Link>
        </aside>
      </div></section>


      <section className="border-y border-border bg-charcoal/60 py-20">
        <div className="container-x">
          <SectionHeading eyebrow="Local FAQs" title={`Garage Floor Coating Questions From ${p.name} Homeowners`} />
          <div className="mt-10">
            <FAQAccordion items={withObjectionFaqs(p.faqs)} />
          </div>
        </div>
      </section>

      <section className="container-x py-20">
        <SectionHeading eyebrow={`Coating Services in ${p.name}`} title={`Epoxy & Polyaspartic Systems We Install in ${p.name}`} />
        <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s) => {
            const anchor = `${s.name} in ${p.name}`;
            return (
              <Link
                key={s.slug}
                to={`/services/${s.slug}` as string}
                className="group rounded-sm border border-border bg-charcoal p-5 hover:border-copper"
              >
                <p className="text-silver-light font-semibold group-hover:text-copper">{anchor}</p>
                <p className="mt-1.5 text-sm text-silver">{s.short}</p>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="container-x py-20">
        <SectionHeading eyebrow="Also Serving" title="Other Calgary Region Cities We Coat" />
        <div className="mt-10 grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {nearby.map((c) => (
            <Link
              key={c.slug}
              to={`/service-areas/${c.slug}` as string}
              className="rounded-sm border border-border bg-charcoal p-4 text-center hover:border-copper"
            >
              <p className="text-sm font-semibold text-silver-light">{c.name}</p>
              <p className="text-xs text-muted-foreground">{c.state}</p>
            </Link>
          ))}
        </div>
      </section>

      <CTASection variant="light" title={`Free Garage Floor Quotes in ${display}`} />

      <section className="container-x py-20">
        <div className="grid gap-10 lg:grid-cols-2">
          <SectionHeading eyebrow="Free Estimate" title={`Request A ${p.name} Quote`} body="A real estimator visits your space — no high-pressure sales, no upsells." />
          <div className="rounded-md border border-border bg-ivory p-7 md:p-8 shadow-2xl">
            <MultiStepQuoteForm />
          </div>
        </div>
      </section>
    </>
  );
}
